import {getTestProps} from '@benchling/js-test-utils';
import {Alert, Box, Button, Group, Stack, Title} from '@mantine/core';
import React, {useState} from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getInsufficientCreditsAgentTypeError} from 'llm/chat-library/MeteringUtils';
import {getScientificAIDisabledMessage} from 'model-hub/permissions/ModelHubPermissions';
import {PropertyResultsTable} from 'model-hub/prediction-jobs/PropertyResultsTable';
import PredictionRunsList from 'model-hub/prediction-runs/PredictionRunsList';
import {type NormalizedRun} from 'model-hub/prediction-runs/splitPredictionRuns';
import {NO_MODELS_INSTALLED_BANNER_MESSAGE} from 'model-hub/scientific-models/NoModelsInstalledEmptyState';
import type {
  ScientificModelNode,
  ScientificModelsByExternalReference,
} from 'model-hub/scientific-models/ScientificModelMetadata';
import {useScientificModelRegistry} from 'model-hub/scientific-models/useAvailableScientificModels';
import notifications from 'notifications';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import {CenteredSpinner} from 'ui-library/Spinner';
import {withLoadingScreen} from 'util/Promises';
import {assertNever} from 'util/Types';

import type {MoleculeFileQuery, MoleculeFileQueryVariables} from './__generated/MoleculeFileQuery';
import {MOLECULE_FILE_QUERY} from './MoleculeFileState';
import {
  getMoleculePropertyPredictionModels,
  requestPropertyPrediction,
  resolvePropertyPredictionFolderId,
} from './MoleculePredictedPropertiesTabUtils';
import {useGetPropertyPredictionRuns} from './useGetPropertyPredictionRuns';

export interface MoleculePredictedPropertiesTabProps {
  moleculeId: string;
}

enum MoleculePredictedPropertiesViewType {
  FORM = 'FORM',
  RUNS_LIST = 'RUNS_LIST',
  RUN_DETAILS = 'RUN_DETAILS',
}

type MoleculePredictedPropertiesView =
  | {type: MoleculePredictedPropertiesViewType.FORM}
  | {type: MoleculePredictedPropertiesViewType.RUNS_LIST}
  | {type: MoleculePredictedPropertiesViewType.RUN_DETAILS; run: NormalizedRun};

function MoleculePredictedPropertiesTab({moleculeId}: MoleculePredictedPropertiesTabProps): JSX.Element {
  return (
    <Box
      className="react-tab-container"
      role="region"
      aria-label="Property Prediction"
      {...getTestProps({
        component: 'MoleculePredictedPropertiesTab',
        element: 'root',
      })}
    >
      <MoleculePredictedPropertiesTabContent moleculeId={moleculeId} />
    </Box>
  );
}

function MoleculePredictedPropertiesTabContent({moleculeId}: {moleculeId: string}): JSX.Element {
  const registryResult = useScientificModelRegistry();
  const moleculeQueryResult = useBenchlingQuery<MoleculeFileQuery, MoleculeFileQueryVariables>(
    MOLECULE_FILE_QUERY,
    {variables: {fileId: moleculeId}, fetchPolicy: 'cache-only'}
  );

  if (registryResult.type === 'loading' || moleculeQueryResult.type === QueryResultType.PENDING) {
    return <CenteredSpinner />;
  }
  if (registryResult.type === 'error' || moleculeQueryResult.type === QueryResultType.ERROR) {
    return (
      <Alert color="red" role="alert">
        Unable to load models. Please refresh the page.
      </Alert>
    );
  }

  const molecule = moleculeQueryResult.data.molecule;
  if (molecule == null || isInaccessible(molecule)) {
    return (
      <Alert color="red" role="alert">
        You do not have access to view this molecule. Please ask your administrator for access.
      </Alert>
    );
  }

  return (
    <LoadedMoleculePredictedPropertiesTab
      availableModels={getMoleculePropertyPredictionModels(registryResult.registry.availableModels)}
      catalogByExternalReference={registryResult.registry.catalogByExternalReference}
      moleculeFolderId={molecule.folder.id}
      moleculeId={moleculeId}
    />
  );
}

function LoadedMoleculePredictedPropertiesTab({
  availableModels,
  catalogByExternalReference,
  moleculeFolderId,
  moleculeId,
}: {
  availableModels: Array<ScientificModelNode>;
  catalogByExternalReference: ScientificModelsByExternalReference;
  moleculeFolderId: string;
  moleculeId: string;
}): JSX.Element | null {
  const [view, setView] = useState<MoleculePredictedPropertiesView>({
    type: MoleculePredictedPropertiesViewType.RUNS_LIST,
  });

  const {pendingRuns, completedRuns, loading, error, refetch} = useGetPropertyPredictionRuns({
    catalogByExternalReference,
    moleculeId,
  });

  const blockingMessage =
    getScientificAIDisabledMessage() ??
    (availableModels.length === 0 ? NO_MODELS_INSTALLED_BANNER_MESSAGE : undefined);

  const handleNavigateToList = (): void => {
    setView({type: MoleculePredictedPropertiesViewType.RUNS_LIST});
  };

  switch (view.type) {
    case MoleculePredictedPropertiesViewType.FORM:
      return (
        <MoleculePredictedPropertiesForm
          availableModels={availableModels}
          blockingMessage={blockingMessage}
          moleculeFolderId={moleculeFolderId}
          moleculeId={moleculeId}
          onSubmit={() => {
            refetch();
            setView({type: MoleculePredictedPropertiesViewType.RUNS_LIST});
          }}
          onViewAllRuns={handleNavigateToList}
        />
      );
    case MoleculePredictedPropertiesViewType.RUNS_LIST:
      if (error) {
        return (
          <Alert color="red" role="alert">
            Failed to load runs
          </Alert>
        );
      }
      return (
        <PredictionRunsList
          completedRuns={completedRuns}
          disabledMessage={blockingMessage}
          loading={loading}
          onCreateNewRun={() => {
            setView({type: MoleculePredictedPropertiesViewType.FORM});
          }}
          onRunSelected={(run) => {
            setView({type: MoleculePredictedPropertiesViewType.RUN_DETAILS, run});
          }}
          pendingRuns={pendingRuns}
          title="Property Prediction"
        />
      );
    case MoleculePredictedPropertiesViewType.RUN_DETAILS:
      return (
        <MoleculePredictedPropertiesRunDetails
          catalogByExternalReference={catalogByExternalReference}
          onViewAllRuns={handleNavigateToList}
          run={view.run}
        />
      );
    default:
      assertNever(view);
      return null;
  }
}

function MoleculePredictedPropertiesForm({
  availableModels,
  blockingMessage,
  moleculeFolderId,
  moleculeId,
  onSubmit,
  onViewAllRuns,
}: {
  availableModels: Array<ScientificModelNode>;
  blockingMessage: string | undefined;
  moleculeFolderId: string;
  moleculeId: string;
  onSubmit: () => void;
  onViewAllRuns: () => void;
}): JSX.Element {
  const [selectedModelReference, setSelectedModelReference] = useState<string | null>(
    availableModels[0]?.externalReference ?? null
  );
  const [creditError, setCreditError] = useState<{title: string; detail: string} | null>(null);

  const selectedModel =
    availableModels.find((model) => model.externalReference === selectedModelReference) ?? null;
  const canRun = selectedModel != null && blockingMessage == null;

  const handleRun = async (): Promise<void> => {
    if (selectedModel == null || blockingMessage != null) {
      return;
    }
    setCreditError(null);
    try {
      await requestPropertyPrediction({
        folderId: resolvePropertyPredictionFolderId(moleculeFolderId),
        model: selectedModel,
        moleculeId,
      });
      notifications.success(`${selectedModel.displayName} run created.`);
      onSubmit();
    } catch (error) {
      const creditsErr = getInsufficientCreditsAgentTypeError(error);
      if (creditsErr != null) {
        setCreditError(creditsErr);
      } else {
        throw error;
      }
    }
  };

  const handleSelectModel = (externalReference: string): void => {
    setSelectedModelReference(externalReference);
  };

  const handleClickRun = (): void => {
    withLoadingScreen(handleRun());
  };

  return (
    <Stack spacing="sm">
      <Button leftIcon={<BenchlingIcon icon="chevron-left" />} onClick={onViewAllRuns} variant="ghost">
        View all runs
      </Button>
      <Title order={2}>Property Prediction</Title>
      {blockingMessage != null ? (
        <Alert color="yellow" role="alert">
          {blockingMessage}
        </Alert>
      ) : null}
      {creditError != null ? (
        <Alert color="red" role="alert" title={creditError.title}>
          {creditError.detail}
        </Alert>
      ) : null}
      <Group align="flex-end" spacing="sm" grow={true} noWrap={true}>
        {availableModels.length > 0 ? (
          <Box
            {...getTestProps({
              component: 'MoleculePredictedPropertiesTab',
              element: 'modelSelect',
            })}
          >
            <SelectDropdown
              displayName={selectedModel?.displayName ?? 'Select a model'}
              menu={
                <Menu width={MenuWidth.FULL}>
                  {availableModels.map(({externalReference, displayName}) => (
                    <Option
                      key={externalReference}
                      value={displayName}
                      onSelect={() => {
                        handleSelectModel(externalReference);
                      }}
                      isSelected={selectedModelReference === externalReference}
                    >
                      {displayName}
                    </Option>
                  ))}
                </Menu>
              }
              disabled={blockingMessage != null}
            />
          </Box>
        ) : null}
        <Button
          variant="primary"
          disabled={!canRun}
          leftIcon={<BenchlingIcon icon="sparkle" />}
          onClick={handleClickRun}
        >
          Create new run
        </Button>
      </Group>
    </Stack>
  );
}

function MoleculePredictedPropertiesRunDetails({
  catalogByExternalReference,
  onViewAllRuns,
  run,
}: {
  catalogByExternalReference: ScientificModelsByExternalReference;
  onViewAllRuns: () => void;
  run: NormalizedRun;
}): JSX.Element {
  const model = run.modelName != null ? catalogByExternalReference.get(run.modelName) : undefined;
  return (
    <Stack spacing="md">
      <Button leftIcon={<BenchlingIcon icon="chevron-left" />} onClick={onViewAllRuns} variant="ghost">
        View all runs
      </Button>
      <Title order={2}>{run.displayName ?? 'Property prediction results'}</Title>
      <PropertyResultsTable
        properties={run.properties}
        propertyDefinitions={model?.propertyDefinitions ?? []}
      />
    </Stack>
  );
}

export default MoleculePredictedPropertiesTab;
